import { useState, useMemo, useRef, Fragment } from 'react';
import { pricingData } from '../../../mocks/pricingData';

const sortedPricingData = [...pricingData].sort((a, b) =>
  a.destination.localeCompare(b.destination, 'es')
);

const PricingSection = () => {
  const [selectedDestination, setSelectedDestination] = useState('');
  const [selectedCapacity, setSelectedCapacity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const availableLetters = useMemo(() => {
    const letters = new Set(
      sortedPricingData.map((item) => item.destination.charAt(0).toUpperCase())
    );
    return Array.from(letters).sort((a, b) => a.localeCompare(b, 'es'));
  }, []);

  const filteredData = useMemo(() => {
    let data = sortedPricingData;
    if (searchTerm) {
      data = data.filter((item) =>
        item.destination.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (activeLetter) {
      data = data.filter(
        (item) =>
          item.destination.charAt(0).toUpperCase() === activeLetter
      );
    }
    return data;
  }, [searchTerm, activeLetter]);

  const getPrice = (destination: string, capacity: string): number | null => {
    const destData = sortedPricingData.find(
      (item) => item.destination === destination
    );
    if (!destData) return null;
    switch (capacity) {
      case '6a8':
        return destData.price6a8;
      case '9a13':
        return destData.price9a13;
      case '14a19':
        return destData.price14a19;
      default:
        return null;
    }
  };

  const capacityLabel = (cap: string) => {
    switch (cap) {
      case '6a8':
        return '6 a 8 plazas';
      case '9a13':
        return '9 a 13 plazas';
      case '14a19':
        return '14 a 19 plazas';
      default:
        return '';
    }
  };

  const currentPrice =
    selectedDestination && selectedCapacity
      ? getPrice(selectedDestination, selectedCapacity)
      : null;

  const handleLetterClick = (letter: string) => {
    if (activeLetter === letter) {
      setActiveLetter(null);
    } else {
      setActiveLetter(letter);
      setSearchTerm('');
    }
  };

  return (
    <section
      id="precios"
      className="py-20 bg-gradient-to-br from-gray-50 to-white"
    >
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-teal-100 text-teal-800 px-6 py-2 rounded-full text-sm font-semibold mb-4">
            <i className="ri-price-tag-3-line mr-2"></i>
            TARIFAS TRANSPARENTES
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Consulta Precios al Instante
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Selecciona tu destino y n&uacute;mero de plazas para conocer el
            precio exacto de tu traslado desde el aeropuerto
          </p>
        </div>

        {/* Price Calculator */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              <i className="ri-calculator-line text-teal-600 mr-3"></i>
              Calculadora de Precios
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <i className="ri-map-pin-line text-teal-600 mr-2"></i>
                  Selecciona el Destino
                </label>
                <select
                  value={selectedDestination}
                  onChange={(e) => setSelectedDestination(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">-- Elige tu destino --</option>
                  {sortedPricingData.map((item) => (
                    <option key={item.destination} value={item.destination}>
                      {item.destination}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <i className="ri-group-line text-teal-600 mr-2"></i>
                  N&uacute;mero de Plazas
                </label>
                <select
                  value={selectedCapacity}
                  onChange={(e) => setSelectedCapacity(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">-- Plazas necesarias --</option>
                  <option value="6a8">6 a 8 plazas</option>
                  <option value="9a13">9 a 13 plazas</option>
                  <option value="14a19">14 a 19 plazas</option>
                </select>
              </div>
            </div>

            {currentPrice !== null ? (
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl p-8 text-center">
                <div className="mb-4">
                  <i className="ri-bus-2-line text-4xl mb-2"></i>
                  <h4 className="text-xl font-semibold">
                    {selectedDestination} &mdash;{' '}
                    {capacityLabel(selectedCapacity)}
                  </h4>
                </div>
                <div className="text-5xl font-bold mb-2">
                  {currentPrice.toFixed(2)} &euro;
                </div>
                <p className="text-teal-100 text-lg">
                  Precio por trayecto (solo ida)
                </p>
                <div className="mt-6 flex flex-wrap gap-4 justify-center">
                  <button
                    onClick={() => {
                      const el = document.getElementById('reservas');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line mr-2"></i>
                    Reservar Ahora
                  </button>
                  <a
                    href="https://wa.me/34614932797"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    WhatsApp
                  </a>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-search-line text-4xl mb-4 text-gray-300 block"></i>
                <p className="text-lg">
                  Selecciona destino y n&uacute;mero de plazas para ver el
                  precio
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Map Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-5">
              <h3 className="text-xl font-bold text-white text-center">
                <i className="ri-road-map-line mr-3"></i>
                Zona de Cobertura &mdash; Aeropuerto de M&aacute;laga
              </h3>
              <p className="text-teal-100 text-center mt-1 text-sm">
                Cubrimos toda la Costa del Sol y principales destinos de
                Andaluc&iacute;a
              </p>
            </div>
            <div className="w-full h-[420px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d408305.2!2d-5.1!3d36.72!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd7259c5e7d1e0e1%3A0x5fc7de2b3b3b3b3b!2sAeropuerto%20de%20M%C3%A1laga-Costa%20del%20Sol!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mapa zona de cobertura traslados aeropuerto Malaga"
              ></iframe>
            </div>
            <div className="p-4 bg-gray-50 flex flex-wrap gap-4 justify-center text-sm text-gray-600">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-teal-500 inline-block"></span>
                Costa del Sol
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-amber-500 inline-block"></span>
                Costa Tropical
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-rose-500 inline-block"></span>
                Interior Andaluc&iacute;a
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-indigo-500 inline-block"></span>
                Ciudades principales
              </span>
            </div>
          </div>
        </div>

        {/* Full Price Table */}
        <div
          className="bg-white rounded-2xl shadow-lg overflow-hidden"
          ref={tableRef}
        >
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-6">
            <h3 className="text-2xl font-bold text-white text-center">
              <i className="ri-table-line mr-3"></i>
              Tabla Completa de Precios &mdash; Aeropuerto
            </h3>
            <p className="text-teal-100 text-center mt-2">
              Todos los precios son por trayecto (solo ida) desde/hacia el
              Aeropuerto de M&aacute;laga &mdash; Orden alfab&eacute;tico
            </p>
          </div>

          {/* Search + Letter Filter */}
          <div className="p-4 border-b border-gray-100 space-y-3">
            <div className="max-w-md mx-auto relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setActiveLetter(null);
                }}
                placeholder="Buscar destino..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>
            {/* Alphabet Navigation */}
            <div className="flex flex-wrap justify-center gap-1">
              <button
                onClick={() => {
                  setActiveLetter(null);
                  setSearchTerm('');
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-bold cursor-pointer transition-colors whitespace-nowrap ${
                  !activeLetter && !searchTerm
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-teal-100 hover:text-teal-700'
                }`}
              >
                Todos
              </button>
              {availableLetters.map((letter) => (
                <button
                  key={letter}
                  onClick={() => handleLetterClick(letter)}
                  className={`w-8 h-8 flex items-center justify-center rounded-md text-xs font-bold cursor-pointer transition-colors whitespace-nowrap ${
                    activeLetter === letter
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-teal-100 hover:text-teal-700'
                  }`}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800 text-white sticky top-0">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">
                    Aeropuerto
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">
                    <div className="flex flex-col items-center">
                      <i className="ri-group-line text-lg text-teal-300 mb-1"></i>
                      6 a 8
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">
                    <div className="flex flex-col items-center">
                      <i className="ri-group-line text-lg text-teal-300 mb-1"></i>
                      9 a 13
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">
                    <div className="flex flex-col items-center">
                      <i className="ri-group-line text-lg text-teal-300 mb-1"></i>
                      14 a 19
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, index) => {
                  const showLetterHeader =
                    index === 0 ||
                    item.destination
                      .charAt(0)
                      .toUpperCase() !==
                      filteredData[index - 1].destination
                        .charAt(0)
                        .toUpperCase();

                  return (
                    <Fragment key={item.destination}>
                      {showLetterHeader && (
                        <tr key={`letter-${item.destination.charAt(0)}`}>
                          <td
                            colSpan={4}
                            className="px-6 py-2 bg-teal-50 border-l-4 border-teal-500"
                          >
                            <span className="text-sm font-bold text-teal-700 uppercase tracking-wider">
                              {item.destination.charAt(0).toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      )}
                      <tr
                        className={`${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } hover:bg-teal-50 transition-colors`}
                      >
                        <td className="px-6 py-3.5">
                          <div className="flex items-center">
                            <i className="ri-map-pin-line text-teal-600 mr-3 flex-shrink-0"></i>
                            <span className="font-semibold text-gray-900 text-sm">
                              {item.destination}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-3.5 text-center">
                          <span className="text-lg font-bold text-gray-800">
                            {item.price6a8.toFixed(2)} &euro;
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-center">
                          <span className="text-lg font-bold text-gray-800">
                            {item.price9a13.toFixed(2)} &euro;
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-center">
                          <span className="text-lg font-bold text-gray-800">
                            {item.price14a19.toFixed(2)} &euro;
                          </span>
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
                {filteredData.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      <i className="ri-search-line text-3xl text-gray-300 block mb-3"></i>
                      No se encontraron destinos{' '}
                      {searchTerm
                        ? `con "${searchTerm}"`
                        : `con la letra "${activeLetter}"`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 bg-gray-50 border-t border-gray-100 text-center text-sm text-gray-500">
            Mostrando {filteredData.length} de {sortedPricingData.length}{' '}
            destinos &mdash; Ordenados alfab&eacute;ticamente
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-md text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-arrow-left-right-line text-2xl text-teal-600"></i>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              <a href="#precios" className="hover:text-teal-600 transition-colors">
                Ida y Vuelta
              </a>
            </h4>
            <p className="text-gray-600 text-sm">
              Multiplica el precio x2 para viajes de ida y vuelta
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-2xl text-teal-600"></i>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              <a href="#precios" className="hover:text-teal-600 transition-colors">
                Tiempo de Espera
              </a>
            </h4>
            <p className="text-gray-600 text-sm">
              30 minutos gratis, despu&eacute;s desde 30&euro;/hora
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-discount-percent-line text-2xl text-teal-600"></i>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              <a href="#precios" className="hover:text-teal-600 transition-colors">
                Descuentos
              </a>
            </h4>
            <p className="text-gray-600 text-sm">
              5% descuento por reservas anticipadas
            </p>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            &iquest;No encuentras tu destino? &iquest;Necesitas un servicio
            especial?
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="tel:614932797"
              className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              614 932 797
            </a>
            <a
              href="https://wa.me/34614932797"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              Consultar por WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
